import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Target, ArrowsClockwise, FunnelSimple, PaperPlaneTilt,
    Phone, WarningOctagon, EyeSlash, Coins, Lightning, ListBullets,
    Stack, ShieldCheck, Clock,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatINR, formatDate } from "../../lib/format";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import RoleGuard from "../../components/RoleGuard";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { cn as classJoin } from "../../lib/utils";

const ACTIONS = [
    { key: "escalate", label: "Escalate", icon: WarningOctagon, tone: "rose" },
    { key: "schedule_call", label: "Schedule call", icon: Phone, tone: "amber" },
    { key: "send_reminder", label: "Send reminder", icon: PaperPlaneTilt, tone: "indigo" },
    { key: "monitor", label: "Monitor", icon: EyeSlash, tone: "slate" },
];

const TONE_BG = {
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
};

const SCORE_TONE = (s) =>
    s >= 70 ? "text-rose-700"
        : s >= 50 ? "text-amber-700"
            : s >= 30 ? "text-indigo-700"
                : "text-slate-500";

export default function CollectionsPriority() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionFilter, setActionFilter] = useState("");
    const [followupOpen, setFollowupOpen] = useState(null); // {invoice}
    const [reminderBusy, setReminderBusy] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = actionFilter ? { suggested_action: actionFilter } : {};
            const { data: d } = await api.get("/billing/reports/collections/priority", { params });
            setData(d);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load collections"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [actionFilter]);

    const sendReminder = async (invoice) => {
        setReminderBusy(invoice.id);
        try {
            await api.post(`/billing/invoice/${invoice.id}/send-reminder`, {
                template_code: invoice.reminders_sent === 0 ? "collection_reminder_first" : "collection_reminder_second",
            });
            toast.success(`Reminder sent for ${invoice.invoice_number}`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Reminder failed"));
        } finally {
            setReminderBusy(null);
        }
    };

    if (loading && !data) return <div data-testid="collections-page-loading"><PageHeader eyebrow="Collections" title="Loading…" /></div>;
    if (!data) return null;

    const summary = data.summary || {};
    const portfolioOutstanding = data.portfolio_outstanding || 0;

    return (
        <div data-testid="collections-priority-page">
            <PageHeader
                eyebrow="Collection Intelligence"
                title={
                    <span className="flex items-center gap-3">
                        <Target size={22} weight="duotone" className="text-rose-600" />
                        Priority queue
                    </span>
                }
                description="Open invoices ranked by risk score. Each row carries a recommended next action — non-intrusive, billing logic untouched."
                actions={
                    <Button variant="outline" size="sm" onClick={load} className="gap-1.5" data-testid="collections-refresh-btn">
                        <ArrowsClockwise size={13} /> Refresh
                    </Button>
                }
            />

            <div className="px-6 sm:px-8 py-8 space-y-6">
                {/* Action summary tiles */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="collections-action-summary">
                    {ACTIONS.map((a) => {
                        const Icon = a.icon;
                        const count = summary[a.key] || 0;
                        const isActive = actionFilter === a.key;
                        return (
                            <button
                                key={a.key}
                                onClick={() => setActionFilter(isActive ? "" : a.key)}
                                className={classJoin(
                                    "reedos-card p-4 text-left transition-all border-2",
                                    isActive ? `border-${a.tone}-500 ring-1 ring-${a.tone}-200` : "border-transparent hover:border-slate-200"
                                )}
                                data-testid={`collections-action-${a.key}`}
                            >
                                <div className={classJoin("reedos-eyebrow flex items-center gap-1.5", `text-${a.tone}-700`)}>
                                    <Icon size={11} weight="fill" /> {a.label}
                                </div>
                                <div className={classJoin("font-display font-bold reedos-data text-3xl mt-1 leading-none", `text-${a.tone}-700`)}>{count}</div>
                                <div className="text-[11px] text-slate-500 mt-1">{isActive ? "Filtering" : "Click to filter"}</div>
                            </button>
                        );
                    })}
                </div>

                {/* Headline */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Kpi label="Total open invoices" value={data.total} icon={<ListBullets size={11} />} sub={`${actionFilter ? `Filtered to ${actionFilter}` : "All actions"}`} testid="collections-kpi-total" />
                    <Kpi label="Portfolio outstanding" value={formatINR(portfolioOutstanding)} icon={<Coins size={11} />} tone="emerald" sub="Sum of outstanding (excludes TDS)" testid="collections-kpi-portfolio" />
                    <Kpi
                        label="Top risk score"
                        value={data.items[0]?.score?.toFixed(1) || "—"}
                        icon={<Lightning size={11} />}
                        tone={(data.items[0]?.score || 0) >= 70 ? "rose" : (data.items[0]?.score || 0) >= 50 ? "amber" : "slate"}
                        sub={data.items[0]?.invoice_number}
                        testid="collections-kpi-top-score"
                    />
                </div>

                {/* Priority table */}
                <div className="reedos-card overflow-hidden" data-testid="collections-priority-table">
                    <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                        <div className="reedos-eyebrow flex items-center gap-1.5"><Stack size={11} /> Ranked invoices</div>
                        <div className="text-[10px] text-slate-500">Weights: Overdue {data.weights.overdue}% · Outstanding {data.weights.outstanding}% · Lag {data.weights.lag}% · Cooldown {data.weights.reminder}% · TDS {data.weights.tds_pending}%</div>
                    </div>
                    {data.items.length === 0 ? (
                        <EmptyState icon={<Target size={36} weight="thin" />} title="No invoices in this filter" subtitle="Try removing the action filter or refresh." />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr className="text-left">
                                        <th className="px-3 py-2 reedos-eyebrow text-[9px]">Score</th>
                                        <th className="px-3 py-2 reedos-eyebrow text-[9px]">Invoice / Client</th>
                                        <th className="px-3 py-2 reedos-eyebrow text-[9px] text-right">Outstanding</th>
                                        <th className="px-3 py-2 reedos-eyebrow text-[9px] text-right">Days</th>
                                        <th className="px-3 py-2 reedos-eyebrow text-[9px] text-center">Reminders</th>
                                        <th className="px-3 py-2 reedos-eyebrow text-[9px] text-center">TDS open</th>
                                        <th className="px-3 py-2 reedos-eyebrow text-[9px]">Action</th>
                                        <th className="px-3 py-2"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.items.map((r) => {
                                        const actionDef = ACTIONS.find((a) => a.key === r.suggested_action) || ACTIONS[3];
                                        const ActionIcon = actionDef.icon;
                                        return (
                                            <tr key={r.id} className="border-t border-slate-100 reedos-row-hover" data-testid={`collections-row-${r.id}`}>
                                                <td className="px-3 py-2">
                                                    <div className={classJoin("font-display font-bold reedos-data text-lg leading-none", SCORE_TONE(r.score))}>
                                                        {r.score.toFixed(1)}
                                                    </div>
                                                    <ScoreBreakdown components={r.score_components} weights={data.weights} />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <Link to={`/invoices/${r.id}`} className="font-mono font-semibold text-slate-900 hover:underline text-xs" data-testid={`collections-invoice-link-${r.id}`}>
                                                        {r.invoice_number}
                                                    </Link>
                                                    <div className="text-xs">
                                                        <Link to={`/clients/${r.client_id}/ledger`} className="text-slate-700 hover:underline">{r.client_name}</Link>
                                                    </div>
                                                    {r.client_contact_email && <div className="text-[10px] text-slate-500 truncate max-w-[180px]">{r.client_contact_email}</div>}
                                                </td>
                                                <td className="px-3 py-2 text-xs text-right reedos-data font-bold text-slate-900">{formatINR(r.outstanding)}</td>
                                                <td className="px-3 py-2 text-xs text-right">
                                                    <div className={classJoin("font-mono font-semibold", r.days_overdue > 0 ? "text-rose-700" : "text-slate-500")} title="Days overdue">
                                                        {r.days_overdue > 0 ? `${r.days_overdue}d od` : "On time"}
                                                    </div>
                                                    {r.client_avg_lag_days > 0 && (
                                                        <div className="text-[10px] text-slate-500" title="Client's avg payment lag from history">
                                                            lag {r.client_avg_lag_days}d
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 text-xs text-center">
                                                    <div className={classJoin("font-mono font-semibold", r.reminders_sent >= 3 ? "text-rose-700" : r.reminders_sent > 0 ? "text-amber-700" : "text-slate-500")}>
                                                        {r.reminders_sent}
                                                    </div>
                                                    {r.last_reminder_days_ago !== null && (
                                                        <div className="text-[10px] text-slate-500">{r.last_reminder_days_ago}d ago</div>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 text-xs text-center">
                                                    {r.open_tds_count > 0 ? (
                                                        <span className="inline-flex items-center gap-1 text-amber-700 font-mono font-semibold" title="Open TDS lifecycle entries on this invoice's payments">
                                                            <ShieldCheck size={11} weight="fill" /> {r.open_tds_count}
                                                        </span>
                                                    ) : <span className="text-slate-300">—</span>}
                                                </td>
                                                <td className="px-3 py-2 text-xs">
                                                    <span className={classJoin("inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-semibold border", TONE_BG[actionDef.tone])}>
                                                        <ActionIcon size={11} weight="fill" /> {actionDef.label}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 text-xs">
                                                    <RoleGuard
                                                        permission="reminder:send"
                                                        fallback={<span className="text-[10px] text-slate-400">view-only</span>}
                                                    >
                                                        <div className="flex items-center gap-1.5">
                                                            <Button
                                                                size="sm" variant="outline"
                                                                disabled={reminderBusy === r.id}
                                                                onClick={() => sendReminder(r)}
                                                                className="h-7 px-2 text-[10px] gap-1"
                                                                data-testid={`collections-send-reminder-${r.id}`}
                                                                title="Send the next collection reminder via email"
                                                            >
                                                                <PaperPlaneTilt size={10} /> Remind
                                                            </Button>
                                                            <Button
                                                                size="sm" variant="ghost"
                                                                onClick={() => setFollowupOpen({ invoice: r })}
                                                                className="h-7 px-2 text-[10px] gap-1 text-amber-700"
                                                                data-testid={`collections-schedule-call-${r.id}`}
                                                                title="Schedule a follow-up task"
                                                            >
                                                                <Phone size={10} /> Follow up
                                                            </Button>
                                                        </div>
                                                    </RoleGuard>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Assumptions footer */}
                <div className="text-[11px] text-slate-500 leading-relaxed border-t border-slate-100 pt-4" data-testid="collections-assumptions">
                    <strong className="text-slate-700">How scoring works:</strong> blends days overdue (40), outstanding × age (25), historical client lag (15), reminder cooldown (10 — fresh reminders REDUCE score to avoid spam), and pending TDS lifecycle (10).
                    Action ladder: <strong>≥70 escalate</strong> · ≥50 schedule call · ≥30 send reminder · &lt;30 monitor. Three or more reminders auto-escalates regardless of score.
                </div>
            </div>

            <FollowUpDialog
                open={!!followupOpen}
                onClose={() => setFollowupOpen(null)}
                invoice={followupOpen?.invoice}
                onCreated={() => { setFollowupOpen(null); load(); }}
            />
        </div>
    );
}

function ScoreBreakdown({ components, weights }) {
    return (
        <div className="text-[9px] text-slate-400 mt-0.5 font-mono leading-tight" title={`Components — Overdue: ${components.overdue}/${weights.overdue} · Outstanding: ${components.outstanding}/${weights.outstanding} · Lag: ${components.client_lag}/${weights.lag} · Cooldown: ${components.reminder_cooldown}/${weights.reminder} · TDS: ${components.tds_pending}/${weights.tds_pending}`}>
            {components.overdue.toFixed(0)}+{components.outstanding.toFixed(0)}+{components.client_lag.toFixed(0)}+{components.reminder_cooldown.toFixed(0)}+{components.tds_pending.toFixed(0)}
        </div>
    );
}

function FollowUpDialog({ open, onClose, invoice, onCreated }) {
    const [kind, setKind] = useState("call");
    const [dueDate, setDueDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() + 2);
        return d.toISOString().slice(0, 10);
    });
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setKind("call");
            const d = new Date();
            d.setDate(d.getDate() + 2);
            setDueDate(d.toISOString().slice(0, 10));
            setNotes("");
        }
    }, [open]);

    const submit = async () => {
        if (!dueDate) {
            toast.error("Pick a due date");
            return;
        }
        setBusy(true);
        try {
            await api.post("/billing/follow-up/create", {
                invoice_id: invoice.id,
                kind,
                due_date: dueDate,
                notes,
            });
            toast.success("Follow-up scheduled");
            onCreated();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to schedule"));
        } finally {
            setBusy(false);
        }
    };

    if (!open || !invoice) return null;

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent data-testid="follow-up-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Phone size={18} weight="duotone" className="text-amber-600" />
                        Schedule follow-up
                    </DialogTitle>
                    <DialogDescription>
                        For invoice <strong>{invoice.invoice_number}</strong> · {invoice.client_name} · {formatINR(invoice.outstanding)} outstanding.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[10px] font-semibold text-slate-500 uppercase">Type</Label>
                            <select value={kind} onChange={(e) => setKind(e.target.value)} className="mt-1 w-full h-9 px-2 text-sm border border-slate-200 rounded bg-white" data-testid="follow-up-kind">
                                <option value="call">Phone call</option>
                                <option value="email">Email</option>
                                <option value="meeting">Meeting</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div>
                            <Label className="text-[10px] font-semibold text-slate-500 uppercase">Due date</Label>
                            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1 h-9" data-testid="follow-up-due-date" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-[10px] font-semibold text-slate-500 uppercase">Notes</Label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Context for the follow-up — last conversation, contact info, talk track…"
                            rows={3}
                            className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded resize-none"
                            data-testid="follow-up-notes"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} data-testid="follow-up-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={busy} data-testid="follow-up-submit" className="gap-1.5">
                        <Phone size={13} /> Schedule
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Kpi({ label, value, sub, icon, tone = "slate", testid }) {
    const TONE = {
        slate: "text-slate-900", emerald: "text-emerald-700", rose: "text-rose-700",
        amber: "text-amber-700", indigo: "text-indigo-700",
    };
    return (
        <div className="reedos-card p-4" data-testid={testid}>
            <div className="reedos-eyebrow flex items-center gap-1.5">{icon} {label}</div>
            <div className={classJoin("font-display font-bold reedos-data text-2xl mt-1 leading-none", TONE[tone])}>{value}</div>
            {sub && <div className="text-[11px] text-slate-500 mt-1 leading-tight">{sub}</div>}
        </div>
    );
}
